import { useState } from "react";
import { TextArea, TextInput, TextCheck, RichHint } from "../admin/AdminFields";

/**
 * Lightweight list CRUD for Visual Studio side panel.
 * fields: [{ key, label, type?: 'text'|'textarea'|'check', rows? }]
 */
const ListItemEditor = ({
  title,
  items = [],
  fields,
  itemLabel,
  blankItem,
  onChange,
  hint = null,
}) => {
  const [selected, setSelected] = useState(0);
  const safeIndex = Math.min(selected, Math.max(items.length - 1, 0));
  const current = items[safeIndex];

  const updateAt = (index, next) => {
    const list = [...items];
    list[index] = next;
    onChange(list);
  };

  const add = () => {
    const next = [...items, blankItem()];
    onChange(next);
    setSelected(next.length - 1);
  };

  const remove = (index) => {
    const next = items.filter((_, i) => i !== index);
    onChange(next);
    setSelected(Math.max(0, index - 1));
  };

  const move = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= items.length) return;
    const list = [...items];
    [list[index], list[j]] = [list[j], list[index]];
    onChange(list);
    setSelected(j);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          {title} • {items.length}
        </p>
        <button
          type="button"
          onClick={add}
          className="text-sm font-medium text-green dark:text-green-dark underline underline-offset-2"
        >
          + Add
        </button>
      </div>
      {hint}
      <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-1 dark:border-gray-700">
        {items.length === 0 && (
          <li className="px-2 py-3 text-sm text-gray-500">No items yet.</li>
        )}
        {items.map((item, index) => (
          <li key={index}>
            <button
              type="button"
              onClick={() => setSelected(index)}
              className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${
                safeIndex === index
                  ? "bg-green/10 text-green dark:text-green-dark"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              <span className="line-clamp-2 font-medium">
                {itemLabel(item, index)}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {current != null && (
        <div className="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => move(safeIndex, -1)}
              className="rounded border border-gray-300 px-2 py-0.5 text-[11px] dark:border-gray-600"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => move(safeIndex, 1)}
              className="rounded border border-gray-300 px-2 py-0.5 text-[11px] dark:border-gray-600"
            >
              ↓
            </button>
            <button
              type="button"
              onClick={() => remove(safeIndex)}
              className="rounded border border-red-300 px-2 py-0.5 text-[11px] text-red-600"
            >
              Delete
            </button>
          </div>
          {fields.map((field) => {
            const value = current[field.key];
            if (field.type === "check") {
              return (
                <TextCheck
                  key={field.key}
                  label={field.label}
                  checked={Boolean(value)}
                  onChange={(v) =>
                    updateAt(safeIndex, { ...current, [field.key]: v })
                  }
                />
              );
            }
            if (field.type === "textarea") {
              return (
                <TextArea
                  key={field.key}
                  label={field.label}
                  rows={field.rows || 3}
                  value={value || ""}
                  onChange={(v) =>
                    updateAt(safeIndex, { ...current, [field.key]: v })
                  }
                />
              );
            }
            return (
              <TextInput
                key={field.key}
                label={field.label}
                value={value ?? ""}
                type={field.type === "number" ? "number" : "text"}
                onChange={(v) =>
                  updateAt(safeIndex, { ...current, [field.key]: v })
                }
              />
            );
          })}
          <RichHint />
        </div>
      )}
    </div>
  );
};

export default ListItemEditor;
